import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaArrowLeft, FaSpinner, FaShieldAlt, FaCreditCard, FaArrowRight, FaTag, FaTruck } from "react-icons/fa";
import Swal from 'sweetalert2';
import SummaryApi from "../common";

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);

  const customerId = sessionStorage.getItem("customer_id");
  const FREE_DELIVERY_THRESHOLD = 499;

  // --- HELPER: FORMAT CURRENCY ---
  const displayCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer)
      toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
  });

  useEffect(() => {
    if (customerId) {
      fetchCartData();
    } else {
      setLoading(false);
    }
  }, [customerId]);

  const fetchCartData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(SummaryApi.getCartItems.url, {
        method: SummaryApi.getCartItems.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({ customer_id: customerId })
      });

      const data = await response.json();

      if (data.status && Array.isArray(data.data)) {
        const transformedData = data.data.map(item => {
          const product = item.product_details || {};
          let imageUrl = "https://via.placeholder.com/150";

          if (product.product_img1) {
            const domain = SummaryApi.backendDomain.replace(/\/$/, "");
            const imgPath = product.product_img1.replace(/^\//, "");
            imageUrl = `${domain}/product/${imgPath}`;
          }

          return {
            cartId: item.cart_id,
            productId: product.product_id,
            name: product.product_name || "Product Name",
            price: Number(product.after_discount_price) || 0, // Sale Price
            originalPrice: Number(product.price) || 0,        // MRP
            quantity: parseInt(item.quantity) || 1,
            image: imageUrl,
            category: product.category_name || product.sub_title || "General",
            stock: parseInt(product.quantity) || 50
          };
        });
        setCartItems(transformedData);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      setError("Failed to load cart items.");
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (cartId, productId, newQuantity) => {
    try {
      setUpdatingItem(cartId);
      const response = await fetch(SummaryApi.updateCartItem.url, {
        method: SummaryApi.updateCartItem.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({
          cart_id: cartId,
          product_id: productId,
          quantity: newQuantity,
          customer_id: customerId
        })
      });

      const data = await response.json();
      if (data.status) {
        setCartItems(prevItems => prevItems.map(item =>
          item.cartId === cartId ? { ...item, quantity: newQuantity } : item
        ));
        Toast.fire({ icon: 'success', title: 'Cart updated' });
      }
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      setUpdatingItem(null);
    }
  };

  const removeItem = async (cartId) => {
    const result = await Swal.fire({
      title: 'Remove item?',
      text: "Are you sure you want to remove this item?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111827',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, remove'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(SummaryApi.removeCartItem.url, {
          method: SummaryApi.removeCartItem.method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': SummaryApi.bearerToken
          },
          body: JSON.stringify({ cart_id: cartId })
        });
        const data = await response.json();
        if (data.status) {
          setCartItems(prevItems => prevItems.filter(item => item.cartId !== cartId));
          window.dispatchEvent(new Event("cart-updated"));
          Toast.fire({ icon: 'success', title: 'Item removed' });
        }
      } catch (error) {
        Swal.fire('Error', 'Something went wrong.', 'error');
      }
    }
  };

  const increaseQuantity = (cartId, productId, currentQuantity) => {
    updateCartItem(cartId, productId, currentQuantity + 1);
  };

  const decreaseQuantity = (cartId, productId, currentQuantity) => {
    if (currentQuantity <= 1) return;
    updateCartItem(cartId, productId, currentQuantity - 1);
  };

  // --- FIXED CALCULATIONS ---
  
  // 1. Calculate Gross Total based on MRP (Original Price)
  const totalMRP = cartItems.reduce((sum, item) => sum + (item.originalPrice * item.quantity), 0);
  
  // 2. Calculate Total Savings (The Discount)
  const totalSavings = cartItems.reduce((sum, item) => {
    const savingsPerItem = item.originalPrice > item.price ? (item.originalPrice - item.price) : 0;
    return sum + (savingsPerItem * item.quantity);
  }, 0);

  // 3. Current Subtotal (Price actually paid for products)
  const currentSubtotal = totalMRP - totalSavings;
  
  // 4. Delivery logic based on final discounted product price
  const deliveryCharge = currentSubtotal > FREE_DELIVERY_THRESHOLD ? 0 : 49;
  
  // 5. Final Payable Total
  const finalPayableTotal = currentSubtotal + deliveryCharge;
  
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = () => {
    Swal.fire({
      title: 'Proceed to Checkout?',
      text: `Total Amount: ${displayCurrency(finalPayableTotal)}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#111827',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Place Order'
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/checkout');
      }
    });
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><FaSpinner className="animate-spin text-4xl text-blue-600" /></div>;
  if (error) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-500">{error}</div>;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-white p-8 rounded-2xl shadow-lg">
          <FaShoppingCart className="w-10 h-10 text-blue-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-3">Your Cart is Empty</h1>
          <Link to="/product" className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700">Start Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 lg:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
            <p className="text-gray-500 mt-1">{totalItems} Items in your bag</p>
          </div>
          <Link to="/product" className="hidden sm:flex items-center text-blue-600 font-medium hover:text-blue-700">
            <FaArrowLeft className="mr-2" /> Continue Shopping
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">

          {/* Left Column: Items */}
          <div className="lg:w-2/3 space-y-4">
            
            {/* Free Delivery Progress */}
            {currentSubtotal < FREE_DELIVERY_THRESHOLD ? (
              <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100 mb-2 flex items-center gap-4">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-full"><FaTruck /></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    Add <span className="text-blue-600 font-bold">{displayCurrency(FREE_DELIVERY_THRESHOLD - currentSubtotal)}</span> more for <span className="text-green-600 font-bold">Free Delivery</span>
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${(currentSubtotal / FREE_DELIVERY_THRESHOLD) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm font-medium">
                <FaTruck /> High-five! You've unlocked Free Delivery
              </div>
            )}

            {cartItems.map((item) => (
              <div key={item.cartId} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <div className="flex gap-4 sm:gap-6">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-gray-50 rounded-xl overflow-hidden p-2">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-contain mix-blend-multiply"
                      onError={(e) => { e.target.src = "https://via.placeholder.com/150"; }}
                    />
                  </div>

                  <div className="flex-1 flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">{item.name}</h3>
                        <p className="text-sm text-gray-500 mb-1">{item.category}</p>
                        {item.originalPrice > item.price && (
                          <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded">
                            {Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}% OFF
                          </span>
                        )}
                      </div>
                      <button onClick={() => removeItem(item.cartId)} className="text-gray-400 hover:text-red-500 p-1">
                        <FaTrash />
                      </button>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-4">
                      <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 w-max">
                        <button
                          onClick={() => decreaseQuantity(item.cartId, item.productId, item.quantity)}
                          disabled={item.quantity <= 1 || updatingItem === item.cartId}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-blue-600 disabled:opacity-50"
                        >
                          <FaMinus size={10} />
                        </button>
                        <div className="w-10 text-center font-semibold text-gray-900 text-sm">
                          {updatingItem === item.cartId ? <FaSpinner className="animate-spin mx-auto text-xs" /> : item.quantity}
                        </div>
                        <button
                          onClick={() => increaseQuantity(item.cartId, item.productId, item.quantity)}
                          disabled={updatingItem === item.cartId}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-blue-600 disabled:opacity-50"
                        >
                          <FaPlus size={10} />
                        </button>
                      </div>

                      <div className="text-right">
                        <div className="flex items-baseline justify-end gap-2">
                          {item.originalPrice > item.price && (
                            <span className="text-sm text-gray-400 line-through">
                              {displayCurrency(item.originalPrice * item.quantity)}
                            </span>
                          )}
                          <span className="text-xl font-bold text-gray-900">
                            {displayCurrency(item.price * item.quantity)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Column: Order Summary */}
          <div className="lg:w-[35%] xl:w-[30%]">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:sticky lg:top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-6">Order Summary</h2>

              <div className="mb-6">
                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <FaTag className="text-gray-400 mr-2" />
                  <input type="text" placeholder="Coupon Code" className="bg-transparent text-sm flex-1 outline-none" />
                  <button className="text-xs font-bold text-blue-600 hover:text-blue-700">APPLY</button>
                </div>
              </div>

              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Total MRP</span>
                  <span className="font-medium text-gray-900">{displayCurrency(totalMRP)}</span>
                </div>

                {totalSavings > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount on MRP</span>
                    <span>- {displayCurrency(totalSavings)}</span>
                  </div>
                )}

                <div className="flex justify-between text-gray-600">
                  <span>Delivery Charges</span>
                  <span className={deliveryCharge === 0 ? "text-green-600 font-medium" : "text-gray-900"}>
                    {deliveryCharge === 0 ? "FREE" : displayCurrency(deliveryCharge)}
                  </span>
                </div>
              </div>

              <div className="border-t border-dashed border-gray-200 pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-base font-bold text-gray-900">Total Amount</span>
                  <span className="text-2xl font-bold text-gray-900">{displayCurrency(finalPayableTotal)}</span>
                </div>
                {totalSavings > 0 && (
                  <p className="text-xs text-green-600 mt-2 text-right font-semibold">
                    You saved {displayCurrency(totalSavings)} on this order
                  </p>
                )}
              </div>

              <button
                onClick={handleCheckout}
                className="hidden lg:flex w-full bg-gray-900 text-white font-bold py-4 rounded-xl hover:bg-black transition-all items-center justify-center gap-2 group shadow-lg"
              >
                Checkout Securely <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-gray-400 justify-center">
                  <FaShieldAlt className="text-green-500 text-sm" /> 100% Secure
                </div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-gray-400 justify-center">
                  <FaCreditCard className="text-blue-500 text-sm" /> Easy Returns
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* MOBILE STICKY CHECKOUT BAR */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 lg:hidden shadow-[0_-4px_10px_rgba(0,0,0,0.05)] z-50">
        <div className="flex gap-4 items-center max-w-7xl mx-auto">
          <div className="flex-1">
            <p className="text-xs text-gray-500 font-medium">Total Amount</p>
            <h3 className="text-xl font-bold text-gray-900">{displayCurrency(finalPayableTotal)}</h3>
          </div>
          <button
            onClick={handleCheckout}
            className="bg-gray-900 text-white px-10 py-3.5 rounded-xl font-bold hover:bg-black flex items-center gap-2 active:scale-95 transition-transform"
          >
            Checkout <FaArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cart;